<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { useStaticAsset } from '@/composables/useStaticAsset'
import { apiPost } from '@/lib/http'
import dayjs from '@/lib/dayjs'
import { pikimoni } from '@/helpers/pikimoni'
import AppNavigation from '@/ui-kit/layout/AppNavigation.vue'
import type { PageContext, Student } from './types'
import ParentChildEditor from '@/ui-kit/parent/ParentChildEditor.vue'
import ParentConnectPikimoniModal from '@/ui-kit/parent/ParentConnectPikimoniModal.vue'
import ParentJoinRequestModal from '@/ui-kit/parent/ParentJoinRequestModal.vue'
import ParentOnboardingModal from '@/ui-kit/parent/ParentOnboardingModal.vue'
import ParentPikimoniSuccessModal from '@/ui-kit/parent/ParentPikimoniSuccessModal.vue'
import ParentReportModal from '@/ui-kit/parent/ParentReportModal.vue'
import ParentSmartbookGrantedModal from '@/ui-kit/parent/ParentSmartbookGrantedModal.vue'
import ParentSmartbookProlongModal from '@/ui-kit/parent/ParentSmartbookProlongModal.vue'
import ParentTeacherDetachModal from '@/ui-kit/parent/ParentTeacherDetachModal.vue'
import RequiresAuthModal from '@/plugins/RequiresAuth/RequiresAuthModal.vue'
import StudentBook from '@/ui-kit/student/StudentBook.vue'
import StudentCard from '@/ui-kit/student/StudentCard.vue'
import StudentChangeCourseModal from '@/ui-kit/student/ChangeCourseModal.vue'
import StudentLoginModal from '@/ui-kit/student/StudentLoginModal.vue'
import StudentShopModal from '@/ui-kit/student/ShopModal.vue'
import StudentSmartBookModal from '@/ui-kit/student/SmartBookModal.vue'
import StudentSmartbookUpgradeModal from '@/ui-kit/student/SmartbookUpgradeModal.vue'
import VPurchaseSmartbook from '@/ui-kit/components/PurchaseSmartbook/PurchaseSmartbook.vue'
import { currentUser } from '@/lib/currentUser'
import type { SmartbookGrantConfirmation } from '@/types/smartbook'

interface UpgradeModalRef {
  $open: (courseId: number, courseName: string, studentId: number, expiryDate?: string) => void
}

interface PurchaseSmartbookRef {
  $open: (opts?: { courseId?: number; courseCodename?: string; tariff?: 'b' | 'e'; studentId?: number }) => void
}

interface ProlongModalRef {
  $open: (params: {
    student_id: number
    student_name: string
    prolong_options?: Record<string, number>
    purchase_expiry_date?: string
    course_id: number | null
    course_title: string
    user_requested?: boolean
  }) => void
}

const ctx = usePageContext<PageContext>()
const { isMobile } = useScreen()
const asset = useStaticAsset()
const { notify } = useNotification()

const students = ref<Student[]>([...ctx.students])
const studentBookVideo = ref<string | null>(null)
const studentToDelete = ref<{ id: number; name: string } | null>(null)
const courseModalStudent = ref<number | null | undefined>(null)
const smartBookModalStudent = ref<number | null | undefined>(null)
const studentWithSmartbook = ref<Student | null>(null)
const studentDeletionErrors = ref<string[]>([])

const childEditor = useTemplateRef<{
  create: (prefill?: { first_name?: string; last_name?: string }) => void
  edit: (student: Student) => void
}>('childEditor')
const connectPikimoni = useTemplateRef<{ open: (studentId: number) => void }>('connectPikimoni')
const loginModal = useTemplateRef<{ open: (student: { id: number; login_token?: string }) => void }>('loginModal')
const prolongModal = useTemplateRef<ProlongModalRef>('prolongModal')
const detachTeacherModal = useTemplateRef<{ $open: (student: Student, teacher: { user_id: number }) => void }>(
  'detachTeacherModal'
)
const upgradeModal = useTemplateRef<UpgradeModalRef>('upgradeModal')
const purchaseSmartbook = useTemplateRef<PurchaseSmartbookRef>('purchaseSmartbook')
const onboardingModal = useTemplateRef<{
  $open: (student: Student, courseName: string, purchaseExpiryDate: string) => void
}>('onboardingModal')
const reportModal = useTemplateRef<{ $open: (payload: unknown) => void }>('reportModal')
const joinRequestModal = useTemplateRef<{ $open: (request: unknown, selectedStudentId?: number) => void }>(
  'joinRequestModal'
)
const smartbookGrantedModal = useTemplateRef<{
  $open: (infoOnly: boolean, request: unknown, selectedStudentId?: number) => void
}>('smartbookGrantedModal')
const shopModal = useTemplateRef<{ $open: (studentId: number, coursePartId: number, tab?: number) => void }>(
  'shopModal'
)

const isAuthorized = computed(() => currentUser.id !== 0)

const courseModalStudentObject = computed(() => {
  return students.value.find(e => e.id === courseModalStudent.value) ?? null
})

const smartBookModalStudentObject = computed(() => {
  return students.value.find(e => e.id === smartBookModalStudent.value) ?? null
})

function computeStudentWithSmartbook(source: Student[]): Student | null {
  return source.reduce<Student | null>((acc, current) => {
    if (!current.smart_book?.purchased || !current.smart_book.purchase_expiry_date) return acc

    const currentDaysToExp = dayjs(current.smart_book.purchase_expiry_date).diff(dayjs(), 'day')
    if (currentDaysToExp < 0) return acc

    const prolongOptions = current.smart_book.prolong_options
    const canProlongSmartbook = prolongOptions ? Object.values(prolongOptions).every(value => value > 0) : false

    if (!canProlongSmartbook && acc) return acc

    if (!acc?.smart_book?.purchase_expiry_date) return current

    const accDaysToExp = dayjs(acc.smart_book.purchase_expiry_date).diff(dayjs(), 'day')
    if (Number.isNaN(accDaysToExp) || currentDaysToExp < accDaysToExp) return current

    return acc
  }, null)
}

function appendDemoStudent() {
  students.value.push({
    demo: true,
    id: 0,
    name: 'Демо Ученик',
    studied_course: {
      course_id: null,
      title: 'I Can Speak'
    },
    cabinet_url: ctx.student_demo_cabinet_url,
    diplomas: [],
    reports: [],
    teachers: []
  })
}

function openRequestModal(studentId?: number) {
  if (ctx.grant_smartbook_requests.length > 0) {
    smartbookGrantedModal.value?.$open(false, ctx.grant_smartbook_requests[0], studentId)
  }

  if (ctx.granted_smartbook_infos.length > 0) {
    smartbookGrantedModal.value?.$open(true, ctx.granted_smartbook_infos[0], studentId)
  }

  if (ctx.join_teacher_requests.length > 0) {
    const request = ctx.join_teacher_requests[0]
    joinRequestModal.value?.$open(request, studentId ?? request.preferable_student_id ?? undefined)
  }
}

function addStudent(student: unknown) {
  students.value.push(student as Student)
  openRequestModal()
}

function updateStudent(student: { id: number; name: string; last_name?: string; [key: string]: unknown }) {
  students.value = students.value.map(current => (current.id === student.id ? { ...current, ...student } : current))
}

function requestDeleteStudent(student: { id: number; name: string }) {
  studentDeletionErrors.value = []
  studentToDelete.value = student
}

function closeDeleteModal() {
  studentToDelete.value = null
}

async function doDeleteStudent() {
  if (!studentToDelete.value) return

  try {
    const response = await apiPost('/parent/request_delete_student', {
      id: studentToDelete.value.id
    })

    if (response.failed) throw response
    window.location.reload()
  } catch (error: unknown) {
    if (typeof error === 'object' && error && 'errors' in error && Array.isArray(error.errors)) {
      studentDeletionErrors.value = error.errors
      return
    }

    let message = 'Произошла ошибка при удалении ребенка. Повторите попытку.'
    if (typeof error === 'object' && error && 'message' in error && typeof error.message === 'string') {
      message = error.message
    }

    studentDeletionErrors.value = [message]
  }
}

async function loginPikimoni(studentId: number) {
  const win = window.open('about:blank', '_blank')
  if (!win) return

  try {
    const url = await pikimoni.getLoginUrl('student', studentId)
    win.location.href = url
  } catch (error: unknown) {
    console.error(error)
    win.close()

    let title = 'Не удалось перейти ко входу в Pikimoni'
    if (typeof error === 'string') title = error
    if (typeof error === 'object' && error && 'message' in error) title = String(error.message)

    notify({ type: 'error', title })
  }
}

function disconnectPikimoni(studentId: number) {
  pikimoni.disconnect('student', studentId)
}

function openProlongModal(student?: Student) {
  const selected = student ?? studentWithSmartbook.value
  if (!selected || !selected.smart_book || !selected.studied_course) return

  prolongModal.value?.$open({
    student_id: selected.id,
    student_name: [selected.last_name ?? '', selected.name].join(' ').trim(),
    prolong_options: selected.smart_book.prolong_options,
    purchase_expiry_date: selected.smart_book.purchase_expiry_date,
    course_id: selected.studied_course.course_id,
    course_title: selected.studied_course.title
  })
}

function openReportsModal(student: Student) {
  reportModal.value?.$open({
    id: student.id,
    name: `${student.last_name ?? ''} ${student.name}`.trim(),
    reports: student.reports,
    diplomas: student.diplomas
  })
}

function onCourseChanged(coursePartObject: { id: number; name: string; first_course_part_id?: number }) {
  const index = students.value.findIndex(e => e.id === courseModalStudent.value)
  if (index < 0) return

  students.value[index] = {
    ...students.value[index],
    studied_course: {
      course_id: coursePartObject.id,
      title: coursePartObject.name,
      first_course_part_id: coursePartObject.first_course_part_id
    }
  }

  courseModalStudent.value = null
}

function clearQuery() {
  const url = new URL(window.location.href)
  url.searchParams.delete('smartbook_upgrade_student')
  url.searchParams.delete('smartbook_purchase_tariff')
  url.searchParams.delete('smartbook_course')
  url.searchParams.delete('smartbook_course_codename')
  window.history.replaceState({}, '', url)
}

function closeStudentBookVideo() {
  studentBookVideo.value = null
}

function openStudentLogin(student: Student) {
  if (!student.login_token) return
  loginModal.value?.open({
    id: student.id,
    login_token: student.login_token
  })
}

function openOnboardingLoginModal(student: { id: number; login_token?: string; [key: string]: unknown }) {
  loginModal.value?.open(student)
}

function createStudentFromJoinRequest(prefill: { first_name?: string; last_name?: string }) {
  childEditor.value?.create(prefill)
}

onMounted(() => {
  const query = new URLSearchParams(window.location.search)

  if (query.has('smartbook_upgrade_student')) {
    const studentId = Number(query.get('smartbook_upgrade_student'))
    const courseId = Number(query.get('smartbook_course_id'))
    const courseName = query.get('smartbook_course_name')
    const student = students.value.find(entry => entry.id === studentId)

    if (student?.smart_book?.purchase_expiry_date && courseName) {
      upgradeModal.value?.$open(courseId, courseName, studentId, student.smart_book.purchase_expiry_date)
    }
  } else if (query.has('smartbook_purchase_tariff')) {
    const tariff = query.get('smartbook_purchase_tariff')

    if (tariff === 'b' || tariff === 'e') {
      const courseCodename = query.get('smartbook_course_codename')
      const legacyCourseId = query.get('smartbook_course')

      if (courseCodename) {
        purchaseSmartbook.value?.$open({ courseCodename, tariff })
      } else if (legacyCourseId) {
        // Deprecated: Use `smartbook_course_codename` instead
        purchaseSmartbook.value?.$open({ courseId: Number(legacyCourseId), tariff })
      } else {
        purchaseSmartbook.value?.$open({ tariff })
      }
    }
  } else if (query.has('purchase_smartbook')) {
    purchaseSmartbook.value?.$open()
  } else {
    openRequestModal()
  }

  if (!isAuthorized.value) {
    appendDemoStudent()
  }

  studentWithSmartbook.value = computeStudentWithSmartbook(students.value)

  const onboardingData = sessionStorage.smartbookGrantOnboarding
  if (onboardingData) {
    sessionStorage.removeItem('smartbookGrantOnboarding')
    try {
      const grant = JSON.parse(onboardingData) as SmartbookGrantConfirmation
      const student = students.value.find(item => item.id === grant.student_id)
      if (student) onboardingModal.value?.$open(student, grant.course_name, grant.purchase_expiry_date)
    } catch (error) {
      console.error('Invalid smartbook grant onboarding data', error)
    }
  }

  const expiringStudentNotDismissed = students.value.find(student => {
    if (!student.smart_book?.purchase_expiry_date) return false

    const daysToExp = dayjs(student.smart_book.purchase_expiry_date).diff(dayjs(), 'day')
    if (daysToExp > 14 || daysToExp <= 0) return false

    const dismissValue = Number(localStorage[`student-${student.id}-prolong-dismissed`])
    if (dismissValue === 0) return false
    if (daysToExp < 2 && dismissValue !== 1) return true
    if (daysToExp > 1 && daysToExp < 4 && dismissValue !== 3) return true
    if (daysToExp > 3 && daysToExp < 8 && dismissValue !== 7) return true
    if (daysToExp > 7 && daysToExp < 15 && dismissValue !== 14) return true

    return false
  })

  if (expiringStudentNotDismissed) {
    openProlongModal(expiringStudentNotDismissed)
  }
})
</script>

<template>
  <div class="account-frame">
    <AppNavigation role="parent" :rules-url="ctx.urls.offer" />

    <div v-cloak class="content cabinet-page">
      <v-notifications position="bottom right" />
      <RequiresAuthModal />

      <ParentChildEditor
        ref="childEditor"
        @create="addStudent"
        @edit="updateStudent"
        @delete="requestDeleteStudent"
        @closed="(id: number) => openRequestModal(id)"
      />

      <ParentConnectPikimoniModal ref="connectPikimoni" />
      <ParentPikimoniSuccessModal />
      <StudentLoginModal ref="loginModal" />
      <ParentSmartbookProlongModal ref="prolongModal" />
      <ParentTeacherDetachModal ref="detachTeacherModal" />
      <StudentSmartbookUpgradeModal ref="upgradeModal" @close="clearQuery" />
      <VPurchaseSmartbook ref="purchaseSmartbook" :students="students" @close="clearQuery" />
      <ParentOnboardingModal ref="onboardingModal" @open-login-modal="openOnboardingLoginModal" />

      <v-modal title="Удалить ребенка?" :model-value="Boolean(studentToDelete)" @update:model-value="closeDeleteModal">
        <h2 v-if="!isMobile" class="text-h3 mr-6">Удалить ребенка?</h2>

        <div v-if="studentToDelete" class="text-center text-title mt-3">
          {{ studentToDelete.name }}
        </div>

        <div class="my-3">
          Будут удалены:<br />
          — Профиль ученика<br />
          — Статистика<br />
          — История обучения
        </div>

        <div v-if="studentDeletionErrors.length > 1" style="max-width: min(400px, 100vw)" class="red-text mb-3">
          <ul v-for="error in studentDeletionErrors" :key="error">
            <li>— {{ error }}</li>
          </ul>
        </div>

        <div v-else-if="studentDeletionErrors.length" style="max-width: min(400px, 100vw)" class="red-text mb-3">
          {{ studentDeletionErrors[0] }}
        </div>

        <div class="flex-adaptive">
          <v-button mode="outlined block" @click="closeDeleteModal">Отмена</v-button>
          <div class="ma-1" />
          <v-button mode="error filled block" @click="doDeleteStudent">Удалить</v-button>
        </div>
      </v-modal>

      <v-modal title="О пособии" :model-value="Boolean(studentBookVideo)" @update:model-value="closeStudentBookVideo">
        <video v-if="studentBookVideo" preload="metadata" controls autoplay class="student-book-video mt-d-6">
          <source :src="studentBookVideo" type="video/mp4" />
        </video>
      </v-modal>

      <ParentReportModal ref="reportModal" />
      <ParentJoinRequestModal
        ref="joinRequestModal"
        :students="students"
        @create-student="createStudentFromJoinRequest"
      />
      <ParentSmartbookGrantedModal
        ref="smartbookGrantedModal"
        :students="students"
        @create-student="() => childEditor?.create()"
      />

      <StudentChangeCourseModal
        :student-id="courseModalStudent"
        :student-object="courseModalStudentObject"
        @update:student-id="courseModalStudent = null"
        @changed="onCourseChanged"
      />

      <StudentSmartBookModal
        :student-id="smartBookModalStudent"
        :student-object="smartBookModalStudentObject"
        @update:student-id="smartBookModalStudent = null"
        @open-login-modal="
          () => {
            if (smartBookModalStudentObject) openStudentLogin(smartBookModalStudentObject)
          }
        "
      />

      <StudentShopModal ref="shopModal" />

      <header class="header">
        <h1 class="header__title">Кабинет родителя</h1>
      </header>

      <div class="students">
        <StudentCard
          v-for="student in students"
          :key="student.id"
          :student="student"
          @edit="() => childEditor?.edit(student)"
          @connect:pikimoni="() => connectPikimoni?.open(student.id)"
          @login:pikimoni="loginPikimoni(student.id)"
          @disconnect:pikimoni="disconnectPikimoni(student.id)"
          @open-login-modal="() => openStudentLogin(student)"
        >
          <StudentBook
            v-if="student.studied_course"
            v-requires-auth="'user'"
            class="games__student-book mt-3"
            :title="student.studied_course.title"
            :href="student.studied_course.buy_studentbook_url"
            :image="student.studied_course.cover_image"
            :video="student.studied_course.buy_studentbook_video"
            :smart-book="student.smart_book"
            :student-id="student.id"
            @click:view="studentBookVideo = student.studied_course?.buy_studentbook_video ?? null"
            @click:change-course="courseModalStudent = student.id"
            @click:shop="
              () => {
                if (student.studied_course?.first_course_part_id) {
                  shopModal?.$open(student.id, student.studied_course.first_course_part_id)
                }
              }
            "
          />

          <div class="d-flex my-3">
            <div class="teachers-card mr-2">
              <span class="mb-1">Учителя</span>

              <div v-if="student.teachers.length" class="d-flex">
                <v-menu-base v-for="teacher in student.teachers" :key="teacher.user_id">
                  <template #activator="{ onclick }">
                    <div class="teachers-card__teacher" @click="onclick">
                      <img :src="teacher.profile_image" />
                    </div>
                  </template>

                  <div class="pa-1">
                    <p class="pa-1">{{ teacher.last_name }} {{ teacher.first_name }}</p>
                    <v-menu-divider />

                    <v-menu-button :href="teacher.link" target="_blank">
                      <v-icon class="mr-2">mdi-open-in-new</v-icon>
                      Открыть визитку
                    </v-menu-button>

                    <v-menu-button mode="error" @click="() => detachTeacherModal?.$open(student, teacher)">
                      <v-icon class="mr-2">mdi-close</v-icon>
                      Открепиться от учителя
                    </v-menu-button>
                  </div>
                </v-menu-base>
              </div>

              <div v-else class="my-auto text-nowrap text-button">
                <v-link href="/online_teaching/find_teacher" mode="primary">Найти учителя</v-link>
              </div>
            </div>

            <div v-if="student.reports" class="reports-card">
              <span class="mb-1">Отчёты и дипломы</span>
              <span class="text-title-bold d-flex">
                <div class="reports-card__number">
                  {{ student.reports.length + student.diplomas.length }}
                </div>
                <div class="flex-spacer" />
                <v-link
                  v-requires-auth="'user'"
                  mode="primary"
                  class="text-caption"
                  @click="() => openReportsModal(student)"
                >
                  Открыть
                </v-link>
              </span>
            </div>
          </div>
        </StudentCard>

        <div class="d-flex flex-column" :style="students.length ? undefined : { flexDirection: 'column-reverse' }">
          <v-button v-if="isAuthorized" mode="large primary" class="mr-auto" @click="() => childEditor?.create()">
            <v-icon class="mr-1">mdi-plus</v-icon>
            Добавить ученика
          </v-button>

          <div class="ma-2" />

          <div v-if="studentWithSmartbook" class="parent-smartbook-ad">
            <div class="d-flex" style="position: relative">
              <div class="parent-smartbook-ad__body mr-3">
                <h2 class="text-title-bold">Продлите УУ {{ studentWithSmartbook.studied_course?.title }} выгодно!</h2>
                <p class="mt-half">
                  для ученика
                  <b>{{ studentWithSmartbook.last_name }} {{ studentWithSmartbook.name }}</b>
                </p>
                <p class="mt-2">Доступ до 3 месяцев</p>
                <p class="text-body-bold mt-1 green-text">От 200 рублей!</p>
              </div>

              <div class="parent-smartbook-ad__img parent-smartbook-ad__img_absolute">
                <img :src="asset('img/smartbook-logo.svg')" height="80" />
              </div>
            </div>

            <div class="d-flex mt-3">
              <v-button mode="warn filled" @click="() => openProlongModal()">Продлить</v-button>
            </div>
          </div>

          <div v-else class="parent-smartbook-ad">
            <div class="d-flex">
              <div class="parent-smartbook-ad__body mr-3">
                <h2 class="text-body-bold">Умный Учебник<br />Я говорю по-английски / I Can Speak</h2>
                <ul class="mt-2">
                  <li>— Электронное приложение</li>
                  <li class="mt-1">— Обучающие мультфильмы</li>
                  <li class="mt-1">— Интерактивные задания</li>
                  <li class="mt-1">— Мотивационные кейсы</li>
                </ul>
              </div>

              <div class="parent-smartbook-ad__img">
                <img :src="asset('img/smartbook-logo.svg')" />
              </div>
            </div>

            <div class="d-flex mt-3">
              <v-button mode="warn filled block" :href="ctx.smartbook_demo_url" target="_blank">Попробовать</v-button>
              <div class="ma-half" />
              <v-button mode="warn outlined block" :href="ctx.urls.smartbook_info">Подробнее</v-button>
            </div>
          </div>
        </div>
      </div>

      <v-footer />
    </div>
  </div>
</template>

<style lang="scss">
@use '@/styles/pages/parent/cabinet.scss' as *;
</style>
